/**
 * Turning anything that went wrong into one short, safe line of user-facing text.
 *
 * The rule this module enforces: the UI shows an error *code* and a plain-English
 * *explanation* — never a response body. Upstream failures do not always come
 * back as our JSON envelope; a Cloudflare 504/524, an nginx 502 or a WAF block
 * returns a full HTML document, and dumping `await res.text()` into the status
 * pill printed an entire `<!DOCTYPE html>` page under the URL field.
 *
 * Auto-imported by Nuxt (utils/), so these are available unqualified everywhere.
 */

// How long a message coming from a response body may be before it is cut off.
// Long enough for a real sentence from our API, short enough that nothing can
// unfold into a wall of text in the pill.
const MAX_DETAIL_LENGTH = 180

/**
 * Does this text look like a markup document (HTML/XML) rather than a message?
 * Error pages from CDNs, proxies and origin servers all start life this way.
 */
export const looksLikeMarkup = (text: string): boolean => {
  if (typeof text !== 'string') return false
  const trimmed = text.trim()
  if (trimmed === '') return false
  // A leading `<` is the giveaway for a served document…
  if (/^(<|&lt;|\uFEFF\s*<)/i.test(trimmed)) return true
  // …but the body may be preceded by junk, so also look for the tags an error
  // page inevitably contains anywhere in the text.
  // No whitespace is allowed between `<` and the tag name: `x < p` is prose,
  // `<p` is markup.
  return /<\/?(!doctype|!--|html|head|body|title|meta|script|style|link|div|span|center|h1|h2|p>|p\s|br|hr|pre|table|img)/i.test(
    trimmed,
  )
}

// The handful of entities an escaped error page realistically contains.
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
}

/**
 * Scrub a candidate message down to a single safe line: no markup, no control
 * characters, no runaway length. Returns '' when nothing usable is left, which
 * callers read as "fall back to the status explanation".
 */
export const sanitizeErrorText = (raw: unknown): string => {
  if (typeof raw !== 'string') return ''
  let text = raw
  // Markup is never shown, not even reduced to its text content: the text of a
  // Cloudflare error page is itself pages of boilerplate.
  if (looksLikeMarkup(text)) return ''
  text = text
    .replace(/&#(\d+);/g, (_m, code) => String.fromCharCode(Number(code)))
    .replace(/&[a-z]+;/gi, (m) => ENTITIES[m.toLowerCase()] ?? ' ')
  // Anything that only *becomes* markup after decoding is dropped too.
  if (looksLikeMarkup(text)) return ''
  text = text
    // Control characters, including the newlines/tabs that make a body multi-line.
    .replace(/[\u0000-\u001F\u007F-\u009F]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  // A raw JSON/JS blob is not a message either — `{"statusCode":502,…}`, an
  // array, or a stringified object means the caller had no real text for us.
  if (/^[[{]/.test(text) || text === '[object Object]') return ''
  if (text.length > MAX_DETAIL_LENGTH) {
    const cut = text.slice(0, MAX_DETAIL_LENGTH)
    const lastSpace = cut.lastIndexOf(' ')
    text = (lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…'
  }
  return text
}

// Boilerplate reason phrases. When a body says no more than the status line
// already does, the explanation below is strictly more useful.
const GENERIC_PHRASES = new Set([
  'error',
  'errors',
  'unknown error',
  'unknown',
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'method not allowed',
  'request timeout',
  'request time-out',
  'conflict',
  'payload too large',
  'uri too long',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'internal error',
  'server error',
  'not implemented',
  'bad gateway',
  'service unavailable',
  'service temporarily unavailable',
  'gateway timeout',
  'gateway time-out',
  'origin error',
  'web server is down',
  'connection timed out',
  'origin is unreachable',
  'a timeout occurred',
  'ssl handshake failed',
  'invalid ssl certificate',
  'failed to fetch',
  'load failed',
  'network error',
  'fetch failed',
  'request failed',
  'http error',
  'nginx',
  'cloudflare',
])

const isGeneric = (text: string): boolean =>
  GENERIC_PHRASES.has(
    text
      .toLowerCase()
      .replace(/[.!]+$/, '')
      .replace(/\s+/g, ' ')
      .trim(),
  )

/**
 * Plain-English explanation per HTTP status, phrased around what the user can
 * do about it — the numeric code is always shown next to it.
 */
const STATUS_EXPLANATIONS: Record<number, string> = {
  400: 'That request was not valid. Check the URL and try again.',
  401: 'The color service rejected the request as unauthorized.',
  403: 'The request was blocked. Refresh the page to redo the human check, then try again.',
  404: "We couldn't find that page on the color service.",
  405: 'That request is not allowed on this endpoint.',
  408: 'The request timed out before the site responded. Please try again.',
  409: 'That request conflicted with another one. Please try again.',
  413: 'That request was too large to process.',
  414: 'That URL is too long to process.',
  415: 'That content type is not supported.',
  422: "We couldn't process that URL. Check that it points to a public website.",
  423: 'That resource is locked. Please try again later.',
  425: 'The request arrived too early. Please try again.',
  429: 'Too many requests. Please wait a moment before generating another palette.',
  431: 'The request headers were too large.',
  451: 'That site is unavailable for legal reasons.',
  500: 'Something went wrong on our side while extracting colors. Please try again.',
  501: 'That feature is not supported by the color service.',
  502: 'The color service returned an invalid response. Please try again in a moment.',
  503: 'The color service is temporarily unavailable. Please try again shortly.',
  504: 'The color service took too long to respond. That site may be slow to load; please try again.',
  505: 'That HTTP version is not supported.',
  507: 'The color service ran out of storage handling this request.',
  508: 'The color service detected a loop handling this request.',
  // Cloudflare edge codes: precisely the responses that arrive as an HTML error
  // page rather than as our JSON envelope.
  520: 'The color service returned an unexpected response. Please try again.',
  521: 'The color service is down right now. Please try again shortly.',
  522: 'The connection to the color service timed out. Please try again.',
  523: 'The color service could not be reached. Please try again shortly.',
  524: 'The color service took too long to answer. That site may be slow to load; please try again.',
  525: 'The secure connection to the color service failed. Please try again.',
  526: 'The color service presented an invalid certificate.',
  527: 'The connection to the color service was interrupted. Please try again.',
  530: 'The color service is unreachable right now. Please try again shortly.',
}

/** Explanation for a status code, falling back to its class (4xx / 5xx). */
export const explainHttpStatus = (status: number): string => {
  const known = STATUS_EXPLANATIONS[status]
  if (known) return known
  if (status >= 500) return 'The color service is having trouble right now. Please try again in a moment.'
  if (status >= 400) return "That request couldn't be completed. Check the URL and try again."
  if (status >= 300) return 'The color service redirected the request somewhere unexpected.'
  return 'Something went wrong. Please try again.'
}

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'
export const NETWORK_ERROR_MESSAGE = "We couldn't reach the color service. Check your connection and try again."
export const TIMEOUT_ERROR_MESSAGE = 'The request took too long and was stopped. Please try again.'
export const BAD_PAYLOAD_MESSAGE = 'The color service returned an unexpected response. Please try again in a moment.'

/**
 * Compose the final line: `Error 504 — <explanation>`, or just the explanation
 * when there is no meaningful status (network failures, client-side errors).
 */
export const formatHttpError = (status: number | undefined, detail?: string): string => {
  const cleaned = sanitizeErrorText(detail)
  const usable = cleaned && !isGeneric(cleaned) ? cleaned : explainHttpStatus(status ?? 0)
  return status && status >= 100 ? `Error ${status} — ${usable}` : usable
}

// Errors the browser raises when the request never produced a response at all.
const isNetworkError = (error: any): boolean => {
  if (!error) return false
  const name = String(error.name ?? '')
  const message = String(error.message ?? '')
  if (name === 'TypeError' && /fetch|network|load failed|connection/i.test(message)) return true
  return /^(failed to fetch|load failed|network ?error|fetch failed)/i.test(message.trim())
}

const isAbortError = (error: any): boolean =>
  !!error &&
  (error.name === 'AbortError' ||
    error.name === 'TimeoutError' ||
    /\baborted?\b|signal is aborted/i.test(String(error.message ?? '')))

/** Pull a status code out of the many error shapes that reach the UI. */
const statusOf = (error: any): number | undefined => {
  const candidates = [
    error?.statusCode,
    error?.status,
    error?.response?.status,
    error?.data?.statusCode,
    error?.cause?.statusCode,
  ]
  for (const value of candidates) {
    const n = Number(value)
    if (Number.isFinite(n) && n >= 100 && n <= 599) return n
  }
  return undefined
}

/**
 * The single funnel every user-facing error goes through. Accepts literally
 * anything — an `Error`, an h3/ofetch `FetchError`, a string, a raw response
 * body, `undefined` — and always returns one short, markup-free line.
 */
export const toDisplayError = (error: unknown, fallback: string = GENERIC_ERROR_MESSAGE): string => {
  if (error == null) return fallback

  // Copy we wrote ourselves (validation text, verification hints) passes through.
  if (typeof error === 'string') return sanitizeErrorText(error) || fallback

  const err = error as any

  // Messages built by responseError() are already code + explanation.
  if (typeof err.displayMessage === 'string' && err.displayMessage) return err.displayMessage

  if (isAbortError(err)) return TIMEOUT_ERROR_MESSAGE
  if (isNetworkError(err)) return NETWORK_ERROR_MESSAGE
  // A JSON.parse failure means the body was something other than JSON — usually
  // the very HTML page this module exists to keep off the screen.
  if (err.name === 'SyntaxError') return BAD_PAYLOAD_MESSAGE

  const status = statusOf(err)
  const detail =
    err.statusMessage ??
    err.data?.statusMessage ??
    err.data?.message ??
    err.data?.error ??
    (typeof err.message === 'string' ? err.message : undefined)

  if (status) return formatHttpError(status, typeof detail === 'string' ? detail : undefined)

  const clean = sanitizeErrorText(typeof detail === 'string' ? detail : '')
  return clean && !isGeneric(clean) ? clean : fallback
}

/**
 * Wrap an already-safe message in an Error that `toDisplayError` will pass
 * through untouched. For failures we detect ourselves (an unreadable payload,
 * a malformed response) rather than ones the server reported.
 */
export const displayError = (message: string, statusCode = 0): Error & { displayMessage: string; statusCode: number } => {
  const error = new Error(message) as Error & { displayMessage: string; statusCode: number }
  error.name = 'DisplayError'
  error.displayMessage = message
  error.statusCode = statusCode
  return error
}
